/**
 * Initial application onboarding, held as a Copilot chat conversation.
 *
 * After an application is created the page sends the kickoff text, which starts the copilot's
 * "Copilot onboarding" playbook directly (settings.defaultWorkflow, no model call). It welcomes the
 * builder and asks what kind of agent they want. A website/docs FAQ agent goes on to the cards for
 * website links (or skip), documents (or skip), a description when both were skipped, then
 * "setting up your agent" (the builder may leave; they are emailed) and, from the playbook's agent
 * step, the use-case suggestions. Any other kind ends with a card where the builder describes the
 * agent; that is sent to the copilot as an ordinary custom request. Each card is a CUSTOM bot message (`{type: 'CUSTOM', content:
 * {kind, text, slot?, ...}}`, see botcircuits-agent-builder-copilot-native onboarding_tools.py), so
 * it is stored in the transcript and comes back on reload. A card with a `slot` is a playbook
 * question: its answer is sent as data for that variable (requestAttributes actionType
 * `slot_answer`), which the runtime fills without a model call.
 */

export const GOAL_CARD = 'onboarding.goal';
export const REQUEST_CARD = 'onboarding.request';
export const WEBSITES_CARD = 'onboarding.websites';
export const DOCUMENTS_CARD = 'onboarding.documents';
export const DESCRIPTION_CARD = 'onboarding.description';
export const SETUP_CARD = 'onboarding.setup';
export const SUGGESTIONS_CARD = 'onboarding.suggestions';

const SOURCE_ID = /^[a-z0-9][a-z0-9_-]{2,63}$/;
const EXTENSION = /^[a-z0-9]{1,8}$/;
const DEFAULT_ACCEPT = ['pdf', 'doc', 'docx', 'csv', 'txt'];
const SLOT = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;
const GOAL_ID = /^[a-z][a-z0-9_]{0,31}$/;
/** Starts the onboarding playbook: one of its trigger texts, sent exactly. */
export const KICKOFF_TEXT = 'Help me set up my new agent';

const text = (value, limit) => (typeof value === 'string' ? value.trim().slice(0, limit) : '');
const count = (value, fallback, max) => (Number.isInteger(value) && value >= 1 ? Math.min(value, max) : fallback);

/** A public http(s) URL without login details, as a normalized href; throws a readable error otherwise. */
export function normalizeWebsite(value) {
    let input = String(value || '').trim();
    if (!input.includes('://')) input = `https://${input}`;
    let url;
    try { url = new URL(input); } catch (_) { throw new Error('Enter a valid website, such as example.com.'); }
    if (!['https:', 'http:'].includes(url.protocol) || !url.hostname.includes('.') || url.username || url.password) {
        throw new Error('Enter a public HTTP or HTTPS website without login details.');
    }
    return url.href;
}

function websiteOrNull(value) {
    try { return normalizeWebsite(value); } catch (_) { return null; }
}

function sanitizeUseCase(item, index) {
    if (!item || typeof item !== 'object') return null;
    const useCase = {
        id: String(index),
        category: text(item.category, 60) || 'Use case',
        title: text(item.title, 120),
        description: text(item.description, 300),
        prompt: text(item.prompt, 4000),
    };
    return useCase.title && useCase.prompt ? useCase : null;
}

function sanitizeGoal(option) {
    if (!option || typeof option !== 'object' || typeof option.id !== 'string' || !GOAL_ID.test(option.id)) return null;
    const goal = { id: option.id, title: text(option.title, 120), description: text(option.description, 300) };
    return goal.title ? goal : null;
}

/** Validate an onboarding card from the network (a CUSTOM message's content) before rendering it. */
export function sanitizeOnboardingCard(content) {
    if (!content || typeof content !== 'object') return null;
    const base = { kind: content.kind, text: text(content.text, 1000),
        ...(typeof content.slot === 'string' && SLOT.test(content.slot) ? { slot: content.slot } : {}) };
    switch (content.kind) {
        case DESCRIPTION_CARD:
            return base;
        case GOAL_CARD:
            return { ...base, options: (Array.isArray(content.options) ? content.options : []).slice(0, 6).map(sanitizeGoal).filter(Boolean) };
        case REQUEST_CARD:
            return {
                ...base,
                goal: typeof content.goal === 'string' && GOAL_ID.test(content.goal) ? content.goal : 'custom',
                placeholder: text(content.placeholder, 300),
                intro: text(content.intro, 300),
            };
        case SETUP_CARD:
            return { kind: SETUP_CARD, ...sanitizeSetup({ ...content, message: content.text }) };
        case WEBSITES_CARD:
            return { ...base, maxUrls: count(content.maxUrls, 5, 10) };
        case DOCUMENTS_CARD: {
            const accept = (Array.isArray(content.accept) ? content.accept : DEFAULT_ACCEPT)
                .filter(ext => typeof ext === 'string' && EXTENSION.test(ext));
            return { ...base, accept: accept.length ? accept : DEFAULT_ACCEPT, maxFiles: count(content.maxFiles, 5, 10) };
        }
        case SUGGESTIONS_CARD:
            return {
                ...base,
                businessName: text(content.businessName, 120),
                businessSummary: text(content.businessSummary, 800),
                websites: (Array.isArray(content.websites) ? content.websites : []).map(websiteOrNull).filter(Boolean).slice(0, 10),
                documents: (Array.isArray(content.documents) ? content.documents : []).filter(id => typeof id === 'string' && SOURCE_ID.test(id)).slice(0, 10),
                items: (Array.isArray(content.items) ? content.items : []).slice(0, 6).map(sanitizeUseCase).filter(Boolean)
                    .map((item, index) => ({ ...item, id: String(index) })),
                notified: content.notified === true,
            };
        default:
            return null;
    }
}

/** The "setting up your agent" notice from start_onboarding_setup. */
export function sanitizeSetup(data) {
    if (!data || typeof data !== 'object') return null;
    return {
        message: text(data.message, 1000),
        websites: (Array.isArray(data.websites) ? data.websites : []).map(websiteOrNull).filter(Boolean).slice(0, 10),
        documents: (Array.isArray(data.documents) ? data.documents : []).filter(id => typeof id === 'string' && SOURCE_ID.test(id)).slice(0, 10),
    };
}

// What the builder's answers send: `value` fills a playbook card's variable; without one (a card
// the agent showed itself) `message` goes to the copilot. `display` is the chat bubble either way.
export function goalAnswer(goal) {
    return { value: goal.id, message: goal.title, display: goal.title };
}

/** The builder's description of the agent, sent to the copilot as a custom request. */
export function customRequestAnswer(request, description) {
    const value = String(description || '').trim().slice(0, 4000);
    return { message: request.intro ? `${request.intro}\n\n${value}` : value, display: value };
}

export function websitesAnswer(urls) {
    return {
        value: urls,
        message: `My business websites:\n${urls.map(url => `- ${url}`).join('\n')}`,
        display: urls.length === 1 ? `My website: ${urls[0]}` : `My websites:\n${urls.join('\n')}`,
    };
}

export const WEBSITES_SKIPPED = { value: [], message: "I'll skip the website step: I don't have a website to share.", display: 'Skip websites' };

export function documentsAnswer(uploads) {
    return {
        value: uploads.map(u => u.dataSourceId),
        message: `I uploaded these documents as knowledge sources:\n${uploads.map(u => `- ${u.dataSourceId} (${u.fileName})`).join('\n')}`,
        display: `Uploaded ${uploads.map(u => u.fileName).join(', ')}`,
    };
}

export const DOCUMENTS_SKIPPED = { value: [], message: "I'll skip the documents step: no documents to upload.", display: 'Skip documents' };

export function descriptionAnswer(description) {
    const value = String(description || '').trim().slice(0, 2000);
    return { value, message: value, display: value };
}

/** A new knowledge source id for an onboarding document, from its file name. */
export function documentSourceId(fileName, random = Math.random) {
    const stem = String(fileName || '').toLowerCase().replace(/\.[^.]*$/, '').replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '').slice(0, 40);
    const suffix = Math.floor(random() * 36 ** 4).toString(36).padStart(4, '0');
    return `doc_${stem || 'file'}_${suffix}`;
}

export function onboardingPath(appId) {
    return `/app/${encodeURIComponent(appId)}/console?onboarding=1`;
}

const pendingKey = appId => `bc-copilot-native:initial-onboarding:${appId}`;

export function beginInitialOnboarding(appId) {
    try { sessionStorage.setItem(pendingKey(appId), '1'); } catch (_) { /* route state is the fallback */ }
}

export function hasInitialOnboarding(appId) {
    try { return sessionStorage.getItem(pendingKey(appId)) === '1'; } catch (_) { return false; }
}

export function completeInitialOnboarding(appId) {
    try { sessionStorage.removeItem(pendingKey(appId)); } catch (_) { /* optional storage */ }
}
